import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Download, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { downloadAuthed } from "@/lib/api";
import { DepotPicker } from "@/components/DepotPicker";
import { useDepot } from "@/context/DepotContext";
import { useClientImport } from "@/components/clients/useClientImport";
import { FilePick, ColumnMap } from "@/components/clients/ClientImportParts";
import { ImportPreview, ImportResult } from "@/components/clients/ClientImportPreview";

const slug = (s) => (s || "all-depots").toLowerCase().replace(/[^a-z0-9]+/g, "-");

// Company logins with several depots pick which depot to export; everyone else downloads their current view.
export const useClientExport = () => {
  const { isAdmin, depots, depot, nameOf } = useDepot();
  const [open, setOpen] = useState(false);
  const run = async (depotId) => {
    const q = `?depot_id=${depotId || "all"}`;
    try { await downloadAuthed(`/clients/export.csv${q}`, `clients-${slug(nameOf(depotId))}.csv`); setOpen(false); }
    catch (e) { toast.error(e.message || "Could not export clients"); }
  };
  const start = () => (isAdmin && depots.length > 1 ? setOpen(true) : run(depot));
  return { open, setOpen, run, start, initial: depot };
};

export const ExportClientsDialog = ({ open, setOpen, run, initial }) => {
  const [pick, setPick] = useState(initial || "");
  const [busy, setBusy] = useState(false);
  const go = async () => { setBusy(true); await run(pick); setBusy(false); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="export-clients-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Export clients</DialogTitle>
          <DialogDescription>Download a CSV of one depot&apos;s clients, then use Import CSV to load it into another depot.</DialogDescription>
        </DialogHeader>
        <DepotPicker always value={pick} onChange={setPick} noneLabel="All depots" label="Export from" testId="export-depot-select" />
        <DialogFooter>
          <Button data-testid="export-clients-download" onClick={go} disabled={busy} className="rounded-full bg-primary hover:bg-primary/90 text-white">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} Download CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const T = "import-clients";

export const ImportClientsDialog = ({ open, setOpen, onDone }) => {
  const { isAdmin, depots, depot, nameOf } = useDepot();
  const [target, setTarget] = useState(depot || "");
  const imp = useClientImport();
  const choose = isAdmin && depots.length > 1;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) { setTarget(depot || ""); imp.reset(); } }, [open, depot]);

  const go = async () => {
    const r = await imp.run(target);
    if (!r) return;
    toast.success(`Imported ${r.added} client${r.added !== 1 ? "s" : ""}${r.skipped ? ` · ${r.skipped} already there` : ""}`);
    onDone();
  };
  const n = imp.clients.length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="import-clients-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Import clients</DialogTitle>
          <DialogDescription>
            Upload a spreadsheet (CSV or Excel) from Excel, Google Sheets, Xero, MYOB or another app. We match the columns for you — check them, then import.
            Clients already in the depot are skipped and left unchanged.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 min-w-0">
          {choose
            ? <DepotPicker always value={target} onChange={setTarget} label="Import into depot" testId="import-depot-select" />
            : depot && <p className="text-sm flex items-center gap-1.5 text-muted-foreground" data-testid="import-target-depot"><MapPin className="w-4 h-4 text-primary" /> Importing into <span className="font-semibold text-foreground">{nameOf(depot)}</span></p>}
          <FilePick imp={imp} testid={T} />
          {imp.sheet && <ColumnMap imp={imp} testid={T} />}
          {imp.sheet && <ImportPreview imp={imp} testid={T} />}
          {imp.result && <ImportResult r={imp.result} testid={T} />}
        </div>
        {imp.sheet && (
          <DialogFooter>
            <Button data-testid="import-clients-submit" disabled={imp.busy || !n || (choose && !target)} onClick={go} className="rounded-full bg-primary hover:bg-primary/90 text-white">
              {imp.busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import {n} client{n !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
